import type { DataListRow, SortState } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ MULTI-COLUMN SORT OVER AN ARRAY THE SCREEN ALREADY HOLDS.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Asked for across the 15 Sep document — *"sortable by the same rules as we did elsewhere with the
 * multicolumn sort"* — for the settings tables, which are the lists this product does NOT page.
 *
 * ⚠️⚠️ **THE BIG LISTS SORT ON THE SERVER AND MUST GO ON DOING SO.** Tasks, worklogs and contracts
 * send `?sort=` and the database orders the whole set before the page is cut — sorting a page in
 * memory reorders 50 rows out of 300 and calls it sorted, which is the failure CLAUDE.md §3 names.
 * This is for the other kind: a workspace's holidays for one year, its schedule assignments, its
 * statuses. The screen holds every row there is, so there is no page for a sort to lie about.
 *
 * ⚠️ **The order of `sort` IS the priority**, matching `DataList` and `toggleSort`: the newest click
 * is the primary key. A caller passes the array straight through from the header.
 *
 * ⚠️ **Stable, because `Array.prototype.sort` is** (guaranteed since ES2019) — so rows the
 * comparison cannot separate keep the order they arrived in, which is the server's own.
 */
export function sortInMemory<T>(
  rows: readonly T[],
  sort: readonly SortState[],
  /**
   * How to read each sortable key off a row.
   *
   * ⚠️ **A key with no reader is skipped rather than throwing.** A column can gain a `sortKey` in
   * one commit and its reader in the next, and a screen that crashes on the first click is a worse
   * outcome than one that does not sort yet.
   */
  valueOf: Readonly<Record<string, (row: T) => string | number | null | undefined>>,
): T[] {
  if (sort.length === 0) return [...rows];

  return [...rows].sort((left, right) => {
    for (const key of sort) {
      const read = valueOf[key.key];
      if (read === undefined) continue;

      const a = read(left);
      const b = read(right);
      const aEmpty = a === null || a === undefined || a === '';
      const bEmpty = b === null || b === undefined || b === '';

      /**
       * ⚠️⚠️ **EMPTY SORTS LAST IN BOTH DIRECTIONS, WHICH IS WHY IT IS DECIDED HERE AND NOT IN
       * `compare`.** Returning a number from the comparator and then negating it for `desc` would
       * float the blank rows to the top the moment somebody reversed a column — and reversing is
       * asking *"which is the largest"*, never *"show me the rows with nothing in them"*. This
       * return skips the negation below. SQL makes the same choice with `nulls last`, and
       * `task.service.ts` sets it explicitly for the same reason.
       */
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
      /* ⚠️ `a === null` narrows nothing for TypeScript across two variables, so the guard that
         proves both are present to the compiler is this one rather than `aEmpty`. */
      if (a === null || a === undefined || b === null || b === undefined) continue;

      const order = compare(a, b);
      if (order !== 0) return key.direction === 'desc' ? -order : order;
    }
    return 0;
  });
}

/**
 * ⚠️⚠️ **NUMBERS NUMERICALLY, TEXT BY LOCALE.** Two rules, each earned:
 *
 *  * **`localeCompare`**, because this product's lists are Hungarian and byte order puts *Zsolt*
 *    before *Ádám*. `numeric: true` also makes `10` follow `9` in a name that ends in a figure.
 *  * **Numbers are not strings.** A column of hours sorted lexically reads 1, 10, 2 — the classic,
 *    and invisible until somebody has eleven of something.
 */
function compare(left: string | number, right: string | number): number {
  if (typeof left === 'number' && typeof right === 'number') return left - right;

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * ┌───────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A READER FOR THE LISTS WHOSE ROWS HAVE NO SINGLE SOURCE TYPE.            │
 * └───────────────────────────────────────────────────────┘
 *
 * Most screens sort the array they already hold — tasks, contracts, holidays — and read each key
 * off the row's own fields, which is always better: the value is untouched. Two cannot. The leave
 * QUEUE interleaves leave requests with overtime claims, and the agent's data card builds its
 * columns from whatever the tool returned; neither has one source type to read from.
 *
 * ⚠️⚠️ **SO IT READS THE CELL, AND PREFERS `sort` TO `text`.** A cell that formats its value
 * carries the raw one in `sort`; without it this falls back to the text, which is right for a
 * name and wrong for `4:30`. A column whose text would mislead and whose cell has no `sort` is a
 * bug in the caller, not here.
 */
export function byCell(key: string): (row: DataListRow) => string | number | null | undefined {
  return (row) => {
    const cell = row.cells[key];
    if (cell === null || cell === undefined) return '';
    if (typeof cell !== 'object') return cell;
    return cell.sort ?? cell.text ?? '';
  };
}
